import { useEffect, useState, type FormEvent } from "react"
import type { GhostNote } from "../../types"

type GhostNoteFormProps = {
  messageId: number
  messagePreview?: string
  defaultValue?: GhostNote | null
  pending?: boolean
  onSubmit: (payload: {
    summary: string
    confidence_label?: string | null
    confidence_score?: number | null
    sources?: string[]
    details?: Record<string, unknown>
  }) => void
  onCancel: () => void
}

export function GhostNoteForm({ messageId, messagePreview, defaultValue, pending, onSubmit, onCancel }: GhostNoteFormProps) {
  const [summary, setSummary] = useState(defaultValue?.summary ?? "")
  const [confidenceLabel, setConfidenceLabel] = useState(defaultValue?.confidence_label ?? "medium")
  const [confidenceScore, setConfidenceScore] = useState(
    typeof defaultValue?.confidence_score === "number" ? String(defaultValue.confidence_score) : ""
  )
  const [sources, setSources] = useState((defaultValue?.confidence_sources ?? []).join(", "))
  const [details, setDetails] = useState(readGhostDetails(defaultValue))

  useEffect(() => {
    setSummary(defaultValue?.summary ?? "")
    setConfidenceLabel(defaultValue?.confidence_label ?? "medium")
    setConfidenceScore(
      typeof defaultValue?.confidence_score === "number" ? String(defaultValue.confidence_score) : "",
    )
    setSources((defaultValue?.confidence_sources ?? []).join(", "))
    setDetails(readGhostDetails(defaultValue))
  }, [defaultValue])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!summary.trim()) return
    const scoreValue = confidenceScore.trim()
    const numericScore = scoreValue === "" ? null : Number.parseFloat(scoreValue)
    const payloadSources = sources
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
    const payloadDetails = details.trim() ? { text: details.trim() } : undefined
    onSubmit({
      summary: summary.trim(),
      confidence_label: confidenceLabel ? confidenceLabel.toLowerCase() : null,
      confidence_score: Number.isNaN(numericScore) ? null : numericScore,
      sources: payloadSources,
      details: payloadDetails,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mono" style={{ display: "grid", gap: "12px" }}>
      <header className="tiny" style={{ color: "var(--muted)" }}>
        Private note · Message #{messageId}
      </header>
      {messagePreview ? (
        <blockquote
          style={{
            margin: 0,
            padding: "10px 12px",
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(18,18,20,0.4)",
            fontStyle: "normal",
          }}
        >
          {messagePreview}
        </blockquote>
      ) : null}

      <label style={{ display: "grid", gap: "6px" }}>
        <span className="tiny" style={{ color: "var(--muted)" }}>
          Summary
        </span>
        <textarea
          value={summary}
          onChange={(event) => setSummary(event.target.value)}
          rows={3}
          required
          style={{
            padding: "10px",
            border: "1px solid var(--rail)",
            background: "transparent",
            color: "var(--text)",
          }}
        />
      </label>

      <label style={{ display: "grid", gap: "6px" }}>
        <span className="tiny" style={{ color: "var(--muted)" }}>Confidence label</span>
        <select
          value={confidenceLabel ?? ""}
          onChange={(event) => setConfidenceLabel(event.target.value)}
          style={{
            padding: "10px",
            border: "1px solid var(--rail)",
            background: "transparent",
            color: "var(--text)",
          }}
        >
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="">Unspecified</option>
        </select>
      </label>

      <label style={{ display: "grid", gap: "6px" }}>
        <span className="tiny" style={{ color: "var(--muted)" }}>Confidence score (0-1)</span>
        <input
          value={confidenceScore}
          onChange={(event) => setConfidenceScore(event.target.value)}
          placeholder="0.0"
          inputMode="decimal"
          style={{
            padding: "10px",
            border: "1px solid var(--rail)",
            background: "transparent",
            color: "var(--text)",
          }}
        />
      </label>

      <label style={{ display: "grid", gap: "6px" }}>
        <span className="tiny" style={{ color: "var(--muted)" }}>Sources (comma separated)</span>
        <input
          value={sources}
          onChange={(event) => setSources(event.target.value)}
          style={{
            padding: "10px",
            border: "1px solid var(--rail)",
            background: "transparent",
            color: "var(--text)",
          }}
        />
      </label>

      <label style={{ display: "grid", gap: "6px" }}>
        <span className="tiny" style={{ color: "var(--muted)" }}>Details (optional)</span>
        <textarea
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          rows={3}
          style={{
            padding: "10px",
            border: "1px solid var(--rail)",
            background: "transparent",
            color: "var(--text)",
          }}
        />
      </label>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <button
          type="submit"
          className="mono"
          disabled={pending || !summary.trim()}
          style={{
            padding: "10px 16px",
            border: "2px solid var(--rail)",
            background: pending ? "rgba(18,18,20,0.3)" : "transparent",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: pending ? "progress" : "pointer",
            minHeight: "44px",
          }}
        >
          {pending ? "Saving…" : "Save note"}
        </button>
        <button
          type="button"
          className="mono"
          onClick={onCancel}
          disabled={pending}
          style={{
            padding: "10px 16px",
            border: "1px solid var(--rail)",
            background: "transparent",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: pending ? "not-allowed" : "pointer",
            minHeight: "44px",
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function readGhostDetails(note?: GhostNote | null) {
  if (!note?.ghost_note) return ""
  const raw = note.ghost_note as Record<string, unknown>
  return typeof raw["details"] === "string" ? (raw["details"] as string) : ""
}

export default GhostNoteForm
